import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { AiService } from 'src/app/shared/services/ai.service';
import { AccountSetupHelperService } from '../../account-setup-helper.service';
import { lastValueFrom, Subscription } from 'rxjs';
import { safeJsonParse } from 'src/app/shared/utils/safeParseJson';
import { CheckDomainIfExistResult } from 'src/app/components/user-dashboard/portal/dto/portal.output';
import { ApiService } from 'src/app/shared/services';
import { SharedPortalViewService } from 'src/app/components/user-dashboard/portal/view/shared-portal-view.service';
import { environment } from 'src/environments/environment';

export enum DomainAvailibiltyStateEnum {
  IDLE,
  CHECKING,
  AVAILBILE,
  UNAVAILIBLE,
}
@Component({
  selector: 'app-hub-identity-step',
  templateUrl: './hub-identity-step.component.html',
  styleUrls: ['./hub-identity-step.component.css'],
})
export class HubIdentityStepComponent
  implements OnInit, OnDestroy, AfterViewInit {
  readonly domainAvailibiltyStateEnum = DomainAvailibiltyStateEnum;
  readonly environment = environment;
  @Output() back = new EventEmitter<void>();
  @Output() continue = new EventEmitter<void>();

  // Reference to the tagline section so we can smoothly scroll it into view
  @ViewChild('taglineSection') taglineSection?: ElementRef<HTMLElement>;

  // Height (px) of any sticky/fixed header that sits above the scroll area.
  // Adjust this to match your actual header height, or set to 0 if there's none.
  private readonly stickyHeaderOffset = 96;

  subscriptions: Subscription[] = [];

  triggers: {
    tagline: number;
    title: number;
  } = {
      tagline: 0,
      title: 0,
    };
  isTitleGenerating = false;
  isTaglineGenerating = false;
  // domainAvailable: boolean | null = null;
  domainAvailibiltyState: DomainAvailibiltyStateEnum =
    DomainAvailibiltyStateEnum.IDLE;

  form = new FormGroup({
    hubName: new FormControl(''),
    subDomain: new FormControl(''),
    domain: new FormControl(''),
    tagline: new FormControl(''),
  });

  constructor(
    private fb: FormBuilder,
    private aiService: AiService,
    public accountSetupHelperService: AccountSetupHelperService,
    private apiService: ApiService,
    private sharedPortalViewService: SharedPortalViewService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.formChangeListner();

    const identity =
      this.accountSetupHelperService.wizardData.hubInput.hubIdentity;

    if (!identity?.name?.trim()) {
      this.triggerTitleGenerate();
    } else if (!identity?.tagline?.trim() || identity?.taglinePending) {
      if (identity.domain) {
        this.checkDomainAvailability(identity.domain);
      }
      this.triggerTaglineGenerate();
    } else {
      if (identity.domain) {
        this.checkDomainAvailability(identity.domain);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    if (this.checkDomainTimeout) {
      clearTimeout(this.checkDomainTimeout);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  ngAfterViewInit(): void {
    const identity =
      this.accountSetupHelperService.wizardData.hubInput.hubIdentity;

    if (
      identity?.name?.trim() &&
      (!identity?.tagline?.trim() || identity?.taglinePending)
    ) {
      this.scrollToTaglineSection();
    }
  }

  triggerTitleGenerate() {
    if (this.isTitleGenerating || this.isTaglineGenerating) return;
    this.isTitleGenerating = true;
    this.triggers.title++;
  }

  triggerTaglineGenerate() {
    if (this.isTitleGenerating) return;
    this.isTaglineGenerating = true;
    this.triggers.tagline++;
  }
  formChangeListner() {
    // this.form.valueChanges.subscribe(() => {
    //   this.domainAvailable = null;
    // });
    // this.subscriptions.push(
    //   this.form.valueChanges.subscribe((formValue) => {
    //     this.accountSetupHelperService.wizardData.hubInput.hubIdentity = {
    //       ...this.accountSetupHelperService.wizardData.hubInput.hubIdentity,
    //       name: formValue.hubName,
    //       domain: formValue.domain,
    //       subDomain: formValue.domain,
    //       tagline: formValue.tagline
    //     }
    //   })
    // )
    // this.subscriptions.push(
    //   this.form.controls.hubName.valueChanges.subscribe(async (value) => {
    //     await this.checkDomainAvailability(value);
    //   })
    // );
  }

  get titlePrompt() {
    return `
    You are an expert brand naming assistant.

    Your task: Generate a creative **hub name** based on the following information.

    # Idea
    ${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.idea}

    # Audience
    ${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.audience}

    # Goals
    ${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.goals}

    ### Instructions
      - Use a friendly, modern tone suitable for digital hubs or online communities.
      - Make sure the name is short (1–3 words) and memorable.
      - Return **only** a valid string (no explanations, comments, or text before or after).
    
    Example: Code Play
    `;
  }
  get taglinePrompt() {
    return `
    You are an expert branding assistant.

    Your task: Generate a creative **tagline** based on the following information.

    # Hub Name
    ${this.accountSetupHelperService.wizardData.hubInput.hubIdentity.name}

    # Idea
    ${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.idea}

    # Audience
    ${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.audience}

    # Goals
    ${this.accountSetupHelperService.wizardData.hubInput.aiState.aiData.goals}

    ### Instructions
      - Use a friendly, modern tone suitable for digital hubs or online communities.
      - Make sure the name is short (1–3 words) and memorable.
      - The tagline should be inspiring sentence and under 10 words.
      - Return **only** a valid string (no explanations, comments, or text before or after).
    
    Example: Where kids turn code into creativity.
    `;
  }

  onTitleGenerationStart() {
    this.isTitleGenerating = true;
    this.accountSetupHelperService.setAiLoaderMessage('Naming your hub...');
    this.accountSetupHelperService.setAiLoaderState('thinking');

    setTimeout(() => {
      if (this.isTitleGenerating) {
        this.accountSetupHelperService.setAiLoaderMessage('Let me think...');
        this.accountSetupHelperService.setAiLoaderState('writing');
      }
    }, 1000);
  }

  onTitleGenerateDone(hubName: string) {
    this.isTitleGenerating = false;
    if (!hubName || !hubName.trim()) {
      this.accountSetupHelperService.setAiLoaderState('idle');
      this.isTaglineGenerating = false;
      return;
    }

    this.accountSetupHelperService.setAiLoaderMessage(`It's ${hubName}!`, 2500);
    this.accountSetupHelperService.setAiLoaderState('idle');

    this.accountSetupHelperService.wizardData.hubInput.hubIdentity.name =
      hubName;
    this.accountSetupHelperService.wizardData.hubInput.hubIdentity.taglinePending =
      true;
    this.checkDomainAvailability(hubName);
    this.accountSetupHelperService.scheduleSaveProgress();
    this.form.patchValue({
      hubName: hubName,
    });

    // Turn on the highlight immediately so it appears in sync with the scroll,
    // rather than waiting for the tagline field's own onStartGenerating event.
    this.isTaglineGenerating = true;

    // Guide the user's attention down to the tagline section
    this.scrollToTaglineSection();

    setTimeout(() => {
      this.triggerTaglineGenerate();
    }, 1000);
  }

  onTaglineStartGenerating() {
    this.isTaglineGenerating = true;
    if (this.accountSetupHelperService.wizardData.hubInput.hubIdentity) {
      this.accountSetupHelperService.wizardData.hubInput.hubIdentity.taglinePending =
        true;
    }
    this.accountSetupHelperService.scheduleSaveProgress();
  }

  onTaglineFinishGenerating() {
    this.isTaglineGenerating = false;
    if (this.accountSetupHelperService.wizardData.hubInput.hubIdentity) {
      this.accountSetupHelperService.wizardData.hubInput.hubIdentity.taglinePending =
        false;
    }
    this.accountSetupHelperService.scheduleSaveProgress();
  }

  private scrollTimeout: any;

  /**
   * Smoothly scrolls the tagline section into view WITHOUT using
   * element.scrollIntoView(). scrollIntoView() lets the browser pick
   * whichever ancestor it thinks is scrollable, which can conflict with
   * sticky/fixed headers (causing them to flicker, disappear, or the page
   * to jump). Instead we find the actual scrollable container ourselves
   * and scroll it explicitly, offset by the header height.
   */
  private scrollToTaglineSection(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      const target = this.taglineSection?.nativeElement;
      if (!target) {
        return;
      }

      const scrollContainer = this.getScrollableAncestor(target);

      if (scrollContainer) {
        // Scroll an internal container (e.g. modal/wizard body with overflow-y: auto)
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const currentScrollTop = scrollContainer.scrollTop;

        const offsetWithinContainer =
          targetRect.top - containerRect.top + currentScrollTop;

        scrollContainer.scrollTo({
          top: Math.max(offsetWithinContainer - this.stickyHeaderOffset, 0),
          behavior: 'smooth',
        });
      } else {
        // Fall back to scrolling the window itself
        const targetTop =
          target.getBoundingClientRect().top + window.pageYOffset;

        window.scrollTo({
          top: Math.max(targetTop - this.stickyHeaderOffset, 0),
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  /**
   * Walks up the DOM from `el` to find the nearest ancestor that actually
   * scrolls (overflow-y: auto/scroll and scrollHeight > clientHeight).
   * Returns null if none is found, meaning the window itself should scroll.
   */
  private getScrollableAncestor(el: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = el.parentElement;

    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        node.scrollHeight > node.clientHeight;

      if (isScrollable) {
        return node;
      }
      node = node.parentElement;
    }

    return null;
  }

  async isSubdomainAvailible(
    subdomain: string,
    portalId: number = this.sharedPortalViewService.currentWebsite.id
  ) {
    const normalizedSub = (subdomain || '').trim().toLowerCase().replace(/\s+/g, '');
    try {
      const subdomainCheck$ = this.apiService.checkSubdomainAvailability(
        portalId,
        normalizedSub,
        (error) => {
          throw error;
        }
      );
      const subdomainCheck: CheckDomainIfExistResult = await lastValueFrom(
        subdomainCheck$
      );

      return !subdomainCheck.isExist;
    } catch (e) {
      return false;
    }
  }

  private checkDomainTimeout: any; // 👈 add this property to your component class

  checkDomainAvailability = async (name: string) => {
    // clear any previous pending checks
    if (this.checkDomainTimeout) {
      clearTimeout(this.checkDomainTimeout);
    }

    // update state immediately with normalized domain
    const domainName = (name || '').trim().toLowerCase().replace(/\s+/g, '');
    this.form.patchValue(
      {
        domain: `${domainName}.${this.environment.hubDomain}`,
        subDomain: `${domainName}`,
      },
      { emitEvent: true }
    );
    this.accountSetupHelperService.wizardData.hubInput.hubIdentity.domain = `${domainName}`;
    this.accountSetupHelperService.wizardData.hubInput.hubIdentity.subDomain = `${domainName}`;
    this.domainAvailibiltyState = DomainAvailibiltyStateEnum.CHECKING;

    // debounce actual check
    this.checkDomainTimeout = setTimeout(async () => {
      if (!domainName) {
        this.domainAvailibiltyState = DomainAvailibiltyStateEnum.IDLE;
        return;
      }

      const isSubdomainAvailible = await this.isSubdomainAvailible(domainName);

      this.domainAvailibiltyState = isSubdomainAvailible
        ? DomainAvailibiltyStateEnum.AVAILBILE
        : DomainAvailibiltyStateEnum.UNAVAILIBLE;

      this.accountSetupHelperService.scheduleSaveProgress();

      console.log(this.form.value.domain);
    }, 800); // 👈 debounce delay (in ms)
  };

  finish() {
    this.continue.emit();
  }
}